import { useMutation, useQuery } from "@apollo/client";
import { gql, ProgressPageName } from "./types";

export const progressThresholdMap: Record<ProgressPageName, number> = {
  [ProgressPageName.Disclaimers]: 1,
  [ProgressPageName.Onboarding]: 1,
  [ProgressPageName.Welcome]: 1,
  [ProgressPageName.ContributionsOnboarding]: 1,
  [ProgressPageName.CitizenshipConfirmation]: 1,
};

export const completePageMutation = gql(/* GraphQL */ `
  mutation MarkPageAsCompleted($page: ProgressPageName!, $progress: Int!) {
    markPageAsCompleted(page: $page, progress: $progress) {
      id
      progress
    }
  }
`);
const onboardingStatusQuery = gql(/* GraphQL */ `
  query OnboardingStatus {
    user {
      id
      onboardingProgress {
        id
        page
        progress
        updatedAt
      }
    }
  }
`);

export const useOnboardingProgress = (options?: { skip?: boolean }) => {
  const { data, loading, error } = useQuery(onboardingStatusQuery, {
    skip: options?.skip,
  });
  const onboardingProgress = data?.user.onboardingProgress;

  /**
   * Checks if a user has alredy completed a particular onboarding step and thus doesn't need to see it again
   */
  function isPageCompleted(page: ProgressPageName): boolean {
    const pageProgressItem = onboardingProgress?.find(
      (item) => item.page === page,
    );

    if (!pageProgressItem) {
      return false;
    }

    const pageProgress = pageProgressItem.progress ?? 0;
    const isCompleted = pageProgress >= progressThresholdMap[page];

    return isCompleted;
  }

  return {
    isPageCompleted,
    loading,
    error,
    onboardingProgress,
  };
};

export const useMarkPageCompleted = () => {
  const [markPageAsCompleted, { loading }] = useMutation(completePageMutation, {
    refetchQueries: ["OnboardingStatus"],
    awaitRefetchQueries: true,
  });

  async function markPageCompleted(page: ProgressPageName) {
    try {
      const progress = progressThresholdMap[page];

      const response = await markPageAsCompleted({
        variables: {
          page,
          progress,
        },
      });
      return response;
    } catch {}
  }

  return { markPageCompleted, loading };
};
